/**
 * Functions for initializing `axios` and the API request environment
 */

import axios from 'axios';
import axiosRetry, { isNetworkOrIdempotentRequestError } from 'axios-retry';
import _get from 'lodash/get';
import * as Sentry from '@sentry/react';
import { AppName } from 'sl-api-connector/types';
import { initializeMixpanel, track } from 'src/utils/mixpanel';
import { getAppName, isDrive } from 'src/utils/app';
import { v2Refresh } from 'src/store/modules/auth/v2Operations';
import { getAuthVersion } from 'src/utils/authVersion';

/**
 * URLs that contain these following strings are considered non-mutating (even if the request is not a GET request)
 * and will be retried upon failure.
 */
const whitelistedRetryEndpoints = [
  '/AdvancedSearch',
  '/GetConfig',
  '/GetEntityMetadata',
  '/GetLatestUserUploadRequests',
  '/UploadHighRiskKeywords',
  '/api/beaconcontent/', // POST endpoints like GetContentUtilizationSummary; retry on 500 (e.g. empty body)
  '/apiAdManager/adCampaigns/getTargetKeywords',
  '/apiAdManager/adCampaigns/getTargetProducts',
  '/apiAdManager/adCampaigns/getCampaignProducts',
  '/apiAdManager/keywordRecommendations/getRecommendedKeywords/',
  '/apiAdManager/scheduledActions/getScheduledActionByCampaignId',
  '/apiAdManager/scheduledActions/getScheduledActionByPortfolioId',
  '/apiAdManager/scheduledActions/getScheduledActionByEntityId'
];

/**
 * Non-idempotent, mutating endpoints that must NEVER be auto-retried.
 *
 * Re-sending these after a timeout or 5xx duplicates their side effects. For
 * example, `GenerateContent` charges a credit and writes a new content version
 * per SKU; a retried batch mints a second set — the root cause of the content
 * generation "multiple versions from one click" / double-billing incident.
 *
 * Read-style `/api/beaconcontent/` POSTs (status/lookup) stay retryable via
 * `whitelistedRetryEndpoints`; only the mutations below are excluded.
 */
const nonRetryableMutatingEndpoints = [
  '/api/beaconcontent/GenerateContent',
  '/api/beaconcontent/CreateProfile',
  '/api/beaconcontent/UpdateProfile',
  '/api/beaconcontent/SaveAsDraft',
  '/api/beaconcontent/SaveAsMaster',
  '/api/beaconcontent/ArchiveContent',
  '/api/beaconcontent/ExportContent',
  '/api/beaconcontent/CreateProject',
  '/api/beaconcontent/UpdateProject'
];

const AXIOS_RETRIES = 3;

/** Unique message for Sentry when axios exhausts all retries (search for this in Sentry). */
const AXIOS_RETRY_EXHAUSTED_MESSAGE = '[AxiosRetry] Request failed after maximum retries';

/**
 * Determines if a failed request should be retried. Retry does NOT depend on response body.
 * Known edge cases where 500 with no body can still prevent retries unless handled:
 * - CORS: If OPTIONS preflight fails, browser reports "Network Error" with no error.response; we retry via whitelist when URL is whitelisted.
 * - Timeout: Server 500 + Content-Length:0 with slow/improper connection close can surface as ECONNABORTED (no response); we retry for whitelisted URLs.
 * - Empty body itself is fine: status comes from headers, so 500 + Content-Length:0 is retried via the status check above.
 */
const shouldRetryRequest = (err) => {
  try {
    const url = err.config?.url ?? '';

    // Never auto-retry non-idempotent mutations. This takes precedence over the
    // status- and whitelist-based checks below so that a timeout or 5xx on a
    // mutating POST (e.g. GenerateContent) surfaces as a single error instead of
    // being silently re-sent and duplicating side effects / credit charges.
    if (nonRetryableMutatingEndpoints.some((endpoint) => url.includes(endpoint))) {
      return false;
    }

    const isWhitelisted = whitelistedRetryEndpoints.some((endpoint) => url.includes(endpoint));

    // Check response status from headers only; does not depend on response body (works for 500 + Content-Length: 0).
    const status = err.response?.status;
    if (typeof status === 'number') {
      if (status === 401) {
        return false;
      }
      // Retry on 404 (transient not-found) or any 5xx, regardless of HTTP method or body.
      if (status === 404 || (status >= 500 && status <= 599)) {
        return true;
      }
    }

    // No response received (e.g. connection reset, empty body edge case) but request was sent - retry if whitelisted.
    if (!err.response && err.request && isWhitelisted) {
      return true;
    }

    if (isWhitelisted) {
      return true;
    }

    // Fall back to default: network error or 5xx on idempotent request (GET, HEAD, OPTIONS, PUT, DELETE).
    return isNetworkOrIdempotentRequestError(err);
  } catch {
    // Defensive: if retry logic throws (e.g. unexpected error shape), don't retry to avoid repeated failures.
    return false;
  }
};

/**
 * Single-flight V2 refresh promise. When N requests 401 concurrently, all of
 * them await this same promise so we only POST /v2/refresh once. Cleared in
 * the .finally() so the next 401 (after the new token also expires) starts a
 * fresh attempt instead of replaying a stale resolved/rejected promise.
 */
let v2RefreshPromise: Promise<unknown> | null = null;

/**
 * Sentinel that marks an axios config as already-retried after a successful
 * /v2/refresh. Prevents an infinite refresh -> retry -> 401 -> refresh loop
 * if the refreshed token is somehow still rejected by the server.
 */
const DID_REFRESH_RETRY = '__sl_didRefreshRetry';

/** True if the failing request was /api/auth/v2/refresh itself. */
const isRefreshEndpoint = (url) => typeof url === 'string' && url.includes('/api/auth/v2/refresh');

/**
 * This function sets up default `axios` functionality such as the base URL for API requests, default headers,
 * request authentication, and retry functionality.  It should be called before any API requests are made by the
 * application.
 *
 * @param {string} baseURL The base URL to use for API requests
 * @param {func} handleServerError A function that will be called with the status code of error response codes from
 *  the server such as 401 and 500. Used to log out on 401.
 */
export const configureAxios = (baseURL, handleServerError) => {
  axios.defaults.baseURL = baseURL;
  axios.defaults.headers.crossDomain = true;
  axios.defaults.withCredentials = true;

  // Refresh-on-401 interceptor. Runs BEFORE the analytics/Sentry interceptor
  // below so that a transient expired access token never gets surfaced as a
  // user-visible 401 (which the legacy handler converts to a sign-out).
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const config = error?.config;

      // Conditions under which we do NOT attempt a refresh:
      //   - response wasn't a 401 (let the next interceptor handle it)
      //   - the failing call IS /v2/refresh (would recurse)
      //   - we already retried this exact request once
      //   - no config object (can't retry without it)
      //   - this is a V1 session (no sl-refresh-token cookie exists,
      //     so /v2/refresh would just 400 and waste a round-trip)
      if (
        status !== 401 ||
        !config ||
        config[DID_REFRESH_RETRY] ||
        isRefreshEndpoint(config.url) ||
        getAuthVersion() !== 'v2'
      ) {
        return Promise.reject(error);
      }

      try {
        // Single-flight: every concurrent 401 awaits the same in-flight refresh.
        if (!v2RefreshPromise) {
          v2RefreshPromise = v2Refresh().finally(() => {
            v2RefreshPromise = null;
          });
        }
        await v2RefreshPromise;
      } catch (_refreshErr) {
        // Refresh failed (refresh token revoked / expired / Cognito refused).
        // Fall through with the ORIGINAL 401 so the existing handler in
        // App_v2.handleServerError sends the user to /sign_out.
        return Promise.reject(error);
      }

      // Server rotated the sl-auth-token cookie via Set-Cookie. Replay the
      // original request exactly as it was -- method, URL, headers, body
      // are all preserved on `config`. Mark it so a recurring 401 doesn't
      // re-enter this branch.
      config[DID_REFRESH_RETRY] = true;
      return axios(config);
    }
  );

  // response interceptor
  axios.interceptors.response.use(
    (response) => {
      // Retry read requests that return 204 No Content up to three times, waiting 2.5s between attempts.
      // A counter (__noContentRetries) is stored directly on the config object so it persists across retries.
      if (response.status === 204) {
        const method = ((response.config as any).method || '').toLowerCase();
        if (['get', 'head', 'options'].includes(method)) {
          const retryCount: number = (response.config as any).__noContentRetries || 0;
          if (retryCount < 3) {
            return new Promise<void>((resolve) => setTimeout(resolve, 2.5 * 1000)).then(() => {
              (response.config as any).__noContentRetries = retryCount + 1;
              return axios(response.config);
            });
          }
        }
      }

      if (_get(response, 'config.meta.requestStartedAt')) {
        track('response time', {
          status: response.status,
          duration: new Date().getTime() - (response.config as any).meta.requestStartedAt,
          url: response.config.url
        });
      }
      if (process.env.NODE_ENV !== 'development' && (response.config.url.includes('/api/user/GetConfig') || response.config.url.includes('/api/auth/v2/config'))) {
        initializeMixpanel(response.data.appConfig.mixpanelApiKey);
      }
      return response;
    },
    (error) => {
      // Log and surface to Sentry when all retries were exhausted (unique message for filtering)
      const retryState = error?.config?.['axios-retry'];
      if (retryState && retryState.retryCount >= AXIOS_RETRIES) {
        const status = error.response?.status ?? 'network_error';
        const url = _get(error, 'config.url') || _get(error, 'config.baseURL', '');
        console.error(
          `${AXIOS_RETRY_EXHAUSTED_MESSAGE} (${status}): ${url}`,
          error
        );
        Sentry.captureMessage(`${AXIOS_RETRY_EXHAUSTED_MESSAGE} ${url}`, 'error');
      }

      if (error.response && error.response.status) {
        const { status } = error.response;
        handleServerError(status);

        // Capture all errors using Sentry for Drive
        if (isDrive) {
          Sentry.captureException(error);
        }

        if (_get(error, 'config.meta.requestStartedAt')) {
          track('response time', {
            status: error.response.status,
            time: new Date().getTime() - error.config.meta.requestStartedAt,
            url: error.config.url
          });
        }
      }
      return Promise.reject(error);
    }
  );

  // Retry requests that fail up to three times, waiting 2.5 seconds in between retries.
  axiosRetry(axios, {
    retries: AXIOS_RETRIES,
    retryCondition: shouldRetryRequest,
    retryDelay: () => 2.5 * 1000
  });

  // rateLimit(axios, { maxRPS: 12 });
};
